import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import _ from 'lodash';

import Spinner from 'components/Spinner';

// import { useMediaPredicate } from 'react-media-hook';
// import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import './PortfolioMapWidget.css';

function hasLatLong(property) {
	// Updated to work directly with property object (not nested under .property)
	return !!property?.lat && !!property?.long;
}

function getLatLong(property) {
	// Updated to work directly with property object (not nested under .property)
	return [property?.lat, property?.long];
}

const PortfolioMapWidget = (props) => {
	// const isMobile = useMediaPredicate(MOBILE_SIZE);
	// Use portfolio properties from the new state instead of property list
	const { properties, isLoading } = useSelector((state) => ({
		userId: state.user.userData?.id,
		properties: state.propertyManagement.portfolioProperties || [],
		isLoading: state.propertyManagement.isLoadingPortfolioProperties,
	}));

	// Note: We don't need to dispatch here since DashboardOverview already fetches portfolio properties
	// The data will be available via the shared redux state

	// const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
	// const attribution = '&copy; <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank">City of New York CC BY 4.0</a>';

	// Esri world street map, desaturated in CSS to keep the muted grey styling
	// the map had under the retired CARTO basemap. Esri's own light-grey canvas
	// stops publishing imagery at zoom 16, and the portfolio bounds routinely
	// zoom past that when properties sit on neighbouring blocks, so this
	// service is used instead: it serves real tiles through zoom 19.
	const MAX_ZOOM = 19;
	const tileUrl =
		'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';

	// const START_ZOOM = 12;
	const Leaflet = window.L;

	const nycLat = '40.7128';
	const nycLong = '-74.0060';
	const nycCoords = [nycLat, nycLong];

	let bounds = Leaflet.latLngBounds([nycCoords]);
	let center = nycCoords;

	if (properties?.length) {
		let coords = [];
		properties?.forEach((property) => {
			if (hasLatLong(property)) {
				coords.push(getLatLong(property));
			}
		});
		if (coords.length) {
			bounds = Leaflet.latLngBounds(coords);
			bounds = bounds.pad(0.1);
			let firstProperty = properties?.[0];
			if (hasLatLong(firstProperty)) {
				center = getLatLong(firstProperty);
			}
		}
	}

	const [visibleProperties, setVisibleProperties] = useState({});
	const [collapsedGroups, setCollapsedGroups] = useState({});
	const [groupedProperties, setGroupedProperties] = useState({});

	useEffect(() => {
		if (properties?.length) {
			// Group properties by portfolio_set_id
			const grouped = _.groupBy(properties, 'portfolio_set_id');
			setGroupedProperties(grouped);

			// Initialize visibility for all properties
			let visProperies = {};
			properties.forEach((property) => {
				if (hasLatLong(property)) {
					visProperies[property?.id] = true;
				} else {
					visProperies[property?.id] = false;
				}
			});
			setVisibleProperties(visProperies);

			// Initialize all groups as expanded (not collapsed)
			const collapsed = {};
			Object.keys(grouped).forEach((portfolioSetId) => {
				collapsed[portfolioSetId] = false;
			});
			setCollapsedGroups(collapsed);
		}
	}, [properties]);

	const toggleProperty = (propertyId) => {
		let visProperies = Object.assign({}, visibleProperties);
		visProperies[propertyId] = !visProperies[propertyId];
		setVisibleProperties(visProperies);
	};

	const toggleGroup = (portfolioSetId) => {
		setCollapsedGroups((prev) => ({
			...prev,
			[portfolioSetId]: !prev[portfolioSetId],
		}));
	};

	const toggleGroupProperties = (portfolioSetId, checked) => {
		const groupProperties = groupedProperties[portfolioSetId] || [];
		const updatedVisibility = { ...visibleProperties };

		groupProperties.forEach((property) => {
			if (hasLatLong(property)) {
				updatedVisibility[property.id] = checked;
			}
		});

		setVisibleProperties(updatedVisibility);
	};

	const isGroupChecked = (portfolioSetId) => {
		const groupProperties = groupedProperties[portfolioSetId] || [];
		const propertiesWithCoords = groupProperties.filter(hasLatLong);
		if (propertiesWithCoords.length === 0) return false;
		return propertiesWithCoords.every(
			(property) => visibleProperties[property.id]
		);
	};

	const isGroupIndeterminate = (portfolioSetId) => {
		const groupProperties = groupedProperties[portfolioSetId] || [];
		const propertiesWithCoords = groupProperties.filter(hasLatLong);
		if (propertiesWithCoords.length === 0) return false;
		const checkedCount = propertiesWithCoords.filter(
			(property) => visibleProperties[property.id]
		).length;
		return checkedCount > 0 && checkedCount < propertiesWithCoords.length;
	};

	return (
		<div className="portfolio-map-widget">
			{isLoading ? (
				<Spinner />
			) : (
				<>
					{!!properties?.length ? (
						<div className="map-wrapper">
							<MapContainer
								center={center}
								bounds={bounds}
								scrollWheelZoom={false}
								attributionControl={false}
								className="flex-grow-1"
							>
								<TileLayer url={tileUrl} maxZoom={MAX_ZOOM} />
								{_.uniqBy(properties, 'id')?.map((property) => {
									if (
										!visibleProperties[property.id] ||
										!hasLatLong(property)
									) {
										return null;
									}
									return (
										<Marker key={property?.id} position={getLatLong(property)}>
											<Popup>{property?.address}</Popup>
										</Marker>
									);
								})}
							</MapContainer>
							<div className="map-property-list flex-grow-1">
								<h4 className="tw-text-base tw-font-semibold tw-text-text-primary tw-mb-3">Properties</h4>
								{Object.entries(groupedProperties).map(
									([portfolioSetId, groupProperties]) => {
										const portfolioSetName =
											groupProperties[0]?.portfolio_set_name || 'Uncategorized';
										const isCollapsed = collapsedGroups[portfolioSetId];
										const groupChecked = isGroupChecked(portfolioSetId);
										const groupIndeterminate =
											isGroupIndeterminate(portfolioSetId);

										return (
											<div key={portfolioSetId} className="mb-3">
												<div
													className="tw-flex tw-items-center tw-mb-2 tw-cursor-pointer"
												>
													<input
														type="checkbox"
														className="form-check-input tw-mr-2"
														checked={groupChecked}
														ref={(input) => {
															if (input) {
																input.indeterminate = groupIndeterminate;
															}
														}}
														onChange={(e) =>
															toggleGroupProperties(
																portfolioSetId,
																e.target.checked
															)
														}
														onClick={(e) => e.stopPropagation()}
													/>
													<strong
														className="tw-mr-2 tw-text-sm tw-font-semibold tw-text-text-primary tw-select-none"
														onClick={() => toggleGroup(portfolioSetId)}
													>
														{portfolioSetName}
													</strong>
													<i
														className={`bi bi-chevron-${
															isCollapsed ? 'down' : 'up'
														} tw-text-xs tw-text-text-secondary`}
														onClick={() => toggleGroup(portfolioSetId)}
													></i>
												</div>
												<div
													className={`collapse ${!isCollapsed ? 'show' : ''}`}
												>
													{groupProperties.map((property, idx) => (
														<div
															className="tw-flex tw-items-center tw-ml-4 tw-mb-1.5"
															key={`${portfolioSetId}-${property.id}-${idx}`}
														>
															<input
																type="checkbox"
																className="form-check-input tw-mr-2"
																id={`map-prop-${portfolioSetId}-${property.id}-${idx}`}
																disabled={!hasLatLong(property)}
																value={visibleProperties[property?.id] || false}
																checked={
																	visibleProperties[property?.id] || false
																}
																onChange={() => toggleProperty(property.id)}
															/>
															<label
																className="tw-text-xs tw-text-text-primary tw-cursor-pointer tw-mb-0 tw-select-none"
																htmlFor={`map-prop-${portfolioSetId}-${property.id}-${idx}`}
															>
																{property?.address}
															</label>
														</div>
													))}
												</div>
											</div>
										);
									}
								)}
							</div>
						</div>
					) : (
						<div className="map-empty tw-p-8 tw-text-center tw-text-text-secondary bg-white tw-border tw-border-border-01 tw-rounded-[8px]">
							No portfolio properties found
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default PortfolioMapWidget;
